import * as React from 'react';
import classnames from 'classnames';

import OfferBannerContext from '../../context/OfferBannerContext';

import { __t, applyTranslations } from 'utils/language';
import { addThousandsSeparator } from 'utils/helper/common';

import './offer-banner-details-quote.scss';

applyTranslations(require('./offer-banner-details-quote.i18n.json'));

import type { SubcampaignOffer } from 'types';
import type { OfferBannerTextStyle } from '../../types';

type Props = {
  offer: SubcampaignOffer | undefined,
  textStyle?: OfferBannerTextStyle
};

const MSG_KEY = 'offer_banner_details_quote';

const OfferBannerDetailsQuote: React.FC<Props> = ({
  offer,
  textStyle
}: Props) => {
  const context = React.useContext(OfferBannerContext);
  const { creatorProfile, isSubcampaignRevoked, isQuoteProvided, isBrand } = context;

  const renderCreatorQuoteWaiting = (): React.ReactNode => (
    <div className="offer-banner-details-quote__section">
      {__t(`${MSG_KEY}.messages.quote_requested`)}
    </div>
  );

  const renderCreatorQuoteProvided = (): React.ReactNode => (
    <div className="offer-banner-details-quote__section">
      {__t(`${MSG_KEY}.messages.your_provided_quote`)}:&nbsp;
      {addThousandsSeparator(offer?.quote || 0)}€
    </div>
  );

  const renderCreator = (): React.ReactNode => {
    if (isQuoteProvided) {
      return renderCreatorQuoteProvided();
    }

    if (isSubcampaignRevoked) {
      return renderCreatorQuoteWaiting();
    }

    return null;
  };

  const renderBrandQuoteWaiting = (): React.ReactNode => (
    <div className="offer-banner-details-quote__section">
      {__t(`${MSG_KEY}.messages.quote_requested`)}
    </div>
  );

  const renderBrandQuoteProvided = (): React.ReactNode => {
    const textKey = isSubcampaignRevoked
      ? 'quote_was_provided'
      : 'quote_provided_per_request';

    let text = __t(`${MSG_KEY}.messages.${textKey}`, {
      creatorName: creatorProfile?.name
    });

    if (isSubcampaignRevoked) {
      text = `${text}: `;
    }

    return (
      <div className="offer-banner-details-quote__section">
        {text}
        {isSubcampaignRevoked && `${addThousandsSeparator(offer?.quote || 0)}€`}
      </div>
    );
  };

  const renderBrand = (): React.ReactNode => {
    if (!isQuoteProvided) {
      return renderBrandQuoteWaiting();
    }

    return renderBrandQuoteProvided();
  };

  const containerClasses = classnames('offer-banner-details-quote', {
    [`offer-banner-details-quote--${textStyle}`]: true
  });

  return (
    <div className={containerClasses}>
      {
        isBrand
          ? renderBrand()
          : renderCreator()
      }
    </div>
  );
};

export default OfferBannerDetailsQuote;
